import { useState, useEffect } from "react";
import { PageHero } from "../components/ui-kit";
import { Reveal } from "../components/motion";
import { getTestimonials, getVideoTestimonials } from "../lib/api";
import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";

export default function Testimonials() {
  const [items, setItems] = useState([]);
  const [videos, setVideos] = useState([]);
  const [active, setActive] = useState(0);

  useEffect(() => {
    getTestimonials().then(setItems).catch(() => {});
    getVideoTestimonials().then(setVideos).catch(() => {});
  }, []);

  const prev = () => setActive((a) => (a - 1 + items.length) % items.length);
  const next = () => setActive((a) => (a + 1) % items.length);

  return (
    <div data-testid="testimonials-page">
      <PageHero label="Testimonials" title={<>What clients <span className="text-accent">actually say.</span></>}
        subtitle="Real people, real results. Here's what it's like to be coached by Manav." />

      {/* Carousel */}
      {items.length > 0 && (
        <section className="pb-16">
          <div className="max-w-4xl mx-auto px-6 md:px-10">
            <div className="glass rounded-3xl p-10 md:p-14 text-center relative">
              <Quote className="w-12 h-12 text-accent/40 mx-auto mb-6" />
              <div className="flex justify-center gap-1 mb-6">{Array.from({ length: items[active].rating }).map((_, j) => <Star key={j} className="w-5 h-5 fill-accent text-accent" />)}</div>
              <p className="text-xl md:text-2xl font-heading font-medium leading-relaxed mb-8">"{items[active].text}"</p>
              <div className="flex items-center justify-center gap-3">
                <img src={items[active].avatar} alt={items[active].name} loading="lazy" decoding="async" className="w-12 h-12 rounded-full object-cover bg-secondary/40" />
                <div className="text-left">
                  <p className="font-heading font-bold">{items[active].name}</p>
                  <p className="text-sm text-muted-foreground">{items[active].role}</p>
                </div>
              </div>
              <div className="flex justify-center gap-3 mt-8">
                <button onClick={prev} data-testid="testimonial-prev" aria-label="Previous" className="w-11 h-11 rounded-full glass flex items-center justify-center hover:text-accent transition-colors"><ChevronLeft className="w-5 h-5" /></button>
                <button onClick={next} data-testid="testimonial-next" aria-label="Next" className="w-11 h-11 rounded-full glass flex items-center justify-center hover:text-accent transition-colors"><ChevronRight className="w-5 h-5" /></button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Video testimonials */}
      {videos.length > 0 && (
        <section className="py-16 border-t border-border" data-testid="video-testimonials-section">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <h2 className="text-2xl md:text-4xl font-black tracking-tight mb-10">Video Testimonials</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {videos.map((v, i) => (
                <Reveal key={v.id} delay={i * 0.1}>
                  <div className="glass rounded-2xl overflow-hidden" data-testid={`video-testimonial-${v.id}`}>
                    <div className="relative aspect-video bg-black">
                      <video src={v.video_url} poster={v.poster_url || undefined}
                        controls playsInline preload="metadata"
                        className="w-full h-full object-cover" />
                    </div>
                    <div className="p-4">
                      <p className="font-heading font-bold text-sm">{v.name}</p>
                      {v.role && <p className="text-xs text-muted-foreground">{v.role}</p>}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Google-review style grid */}
      <section className="py-16 border-t border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <h2 className="text-2xl md:text-4xl font-black tracking-tight mb-10">More Reviews</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((t, i) => (
              <Reveal key={t.id} delay={i*0.06}>
                <div className="glass rounded-2xl p-7 h-full hover-lift">
                  <div className="flex items-center gap-3 mb-4">
                    <img src={t.avatar} alt={t.name} loading="lazy" decoding="async" className="w-11 h-11 rounded-full object-cover bg-secondary/40" />
                    <div>
                      <p className="font-heading font-bold text-sm">{t.name}</p>
                      <div className="flex gap-0.5">{Array.from({ length: t.rating }).map((_, j) => <Star key={j} className="w-3 h-3 fill-accent text-accent" />)}</div>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">{t.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
